/**
 * Payment Data Builder
 *
 * Costruisce i dati di pagamento nel formato richiesto dal backend per la prenotazione.
 * Queste funzioni sono pure: ricevono input e restituiscono output senza effetti collaterali.
 */

import { larger, subtract, format, round } from 'mathjs'

/**
 * Prepara i dati dei codici sconto per il payload di pagamento
 *
 * Trasforma l'array di codici sconto nel formato richiesto dal backend:
 * - Converte tutti gli importi in stringhe formattate
 * - Estrae solo i campi necessari per il backend
 *
 * @param {Array} discount_codes - Array di codici sconto applicati
 * @param {number} discount_codes_amount - Importo totale dei codici sconto
 * @returns {import('../types').DiscountCodesPaymentData} - Oggetto con amount e array di codici formattati
 */
export function prepare_discount_codes_payment_data(discount_codes, discount_codes_amount) {
  return {
    amount: typeof discount_codes_amount === 'string'
      ? discount_codes_amount
      : format(discount_codes_amount),
    codes: discount_codes.map((d) => ({
      discountCode: d.discountCode,
      amount: typeof d.valueUsed === 'string' ? d.valueUsed : format(d.valueUsed),
      valueNominal: typeof d.valueNominal === 'string' ? d.valueNominal : format(d.valueNominal),
      valueResidual: typeof d.valueResidual === 'string' ? d.valueResidual : format(d.valueResidual),
      expirationDate: d.expirationDate,
    }))
  }
}

/**
 * Prepara i dati completi di pagamento per la prenotazione
 *
 * Calcola gli importi da allocare a ciascun metodo di pagamento seguendo questa logica:
 * 1. **Codici sconto** (svincolati dal flag welfare) - sempre sottratti dal prezzo
 * 2. **Carta di credito** (vincolata al flag welfare) - solo se welfare non copre tutto
 * 3. **Welfare** - importo residuo dopo aver sottratto gli altri pagamenti
 *
 * @param {Object} config - Configurazione per il calcolo
 * @param {string} config.live_price - Prezzo corrente della prenotazione
 * @param {number} config.discount_codes_amount - Totale codici sconto applicati
 * @param {Array} config.discount_codes - Array di codici sconto
 * @param {number} config.credit_card_amount - Importo carta di credito
 * @param {Object} config.integration - Configurazione integrazione (welfare flag)
 * @param {boolean} config.integration.welfare - Se welfare copre tutto il pagamento
 * @param {Object} config.existing_cc_data - Dati carta esistenti (paymentIntentId, nameSurname)
 * @returns {import('../types').BookingPaymentsData} - Oggetto con discountCodes, cc, welfare
 */
export function prepare_booking_payments_data(config) {
  const {
    live_price,
    discount_codes_amount,
    discount_codes,
    credit_card_amount,
    integration,
    existing_cc_data = {}
  } = config

  let amount = live_price

  // Sottrai codici sconto (logica SVINCOLATA da flag welfare)
  if (larger(discount_codes_amount, 0)) {
    amount = subtract(amount, discount_codes_amount)
  }

  // Sottrai importo carta se welfare non copre tutto (logica VINCOLATA a flag welfare)
  if (integration.welfare === false && larger(credit_card_amount, 0)) {
    amount = subtract(amount, credit_card_amount)
  }

  // Prepara i dati finali
  const result = {
    discountCodes: prepare_discount_codes_payment_data(discount_codes, discount_codes_amount),
    cc: {},
    welfare: {
      amount: format(round(amount, 2))
    }
  }

  // Aggiungi dati carta se welfare non copre tutto
  if (integration.welfare === false) {
    result.cc = {
      amount: typeof credit_card_amount === 'string'
        ? credit_card_amount
        : format(credit_card_amount),
      ...existing_cc_data
    }
  }

  return result
}